import { Exercise } from '@/model/app/Exercise';
import { useAuth } from '@/core';
import useExercisesDB, { DBExercises } from '@/services/useExercisesDB';
import useGenre from '@/services/useGenre';
import { ExerciseSection } from '@/model/app/ExerciseSection';
import { reactive, ref, toRefs } from 'vue';
import { isEqual } from 'lodash';
import { Disposition } from '@/model/Disposition';
import i18n from '@/i18n';
import { Core } from 'pouchdb';
import { useTenant } from '@/core';

const setup = () => {
  const { t } = i18n.global;
  const { user } = useAuth();
  const defaultLang = ref(import.meta.env.VITE_DEFAULT_LANG);
  const { database, replicationOptions, replicationToOptions } = useExercisesDB();
  const { tenant } = useTenant();

  const state = reactive<{
    exercise: Exercise & { _rev?: Core.RevisionId | undefined };
    exercises: Exercise[];
  }>({
    exercise: null,
    exercises: null,
  });

  const setLanguage = async (language: string): Promise<void> => {
    defaultLang.value = language;
  };

  const loadAllExercises = async () => {
    await database.getAllData({ include_docs: true, attachments: true }).then((res) => {
      state.exercises = res.rows.map((row) => row.doc);
    });
  };

  // This is used for backward compatibility, previous `default` tasks had empty taskId
  const castTaskId = (taskId: string) => {
    return !+taskId ? '' : taskId;
  };

  const generateId = (
    genre: string,
    task: string = '',
    version: number = 1,
    language: string = defaultLang.value,
  ): string => `${user.value._id}-${genre}-${language}-${castTaskId(task)}-${version}`;

  const generateName = (genreName: string, version: number = 1, taskName: string) => {
    return [genreName, taskName, `${t('EXERCISE.DEFAULT_NAME')}_${version}`].filter(Boolean).join(' - ');
  };

  const createEmptySection = (disposition: Disposition) => ({
    content: null,
    image: null,
    position: disposition.allowImage,
  });

  const createEmpty = (
    genre: string,
    task: string = '',
    version: number = 1,
    language: string = defaultLang.value,
  ): Exercise => {
    const { selectGenre } = useGenre();
    const genreItem = selectGenre(genre);
    const taskItem = genreItem.tasks.find((item) => item.id === task);
    const sections: Record<string, ExerciseSection> = {};
    const checklist: Record<string, boolean> = {};

    genreItem.dispositions.forEach((disposition) => {
      sections[disposition.id] = createEmptySection(disposition);
    });

    if (genreItem.checklist.enabled) {
      genreItem.checklist.options.forEach((item) => {
        checklist[item.id] = false;
      });
    }

    return {
      _id: generateId(genre, task, version, language),
      name: generateName(genreItem.name, version, taskItem?.name),
      genre,
      task: castTaskId(task),
      version,
      language,
      sections,
      checklist,
      user: user.value._id,
      createdAt: Date.now(),
      modifiedAt: Date.now(),
      attachments: [],
      productId: tenant.value.customElementOptions?.parentNamespace || tenant.value.namespace,
      entryType: DBExercises,
    };
  };

  const exists = (
    genre: string,
    task: string = '',
    version: number = 1,
    language: string = defaultLang.value,
  ): Promise<boolean> =>
    database
      .getData(generateId(genre, task, version, language))
      .then(() => true)
      .catch(() => false);

  const getBy = async (
    genreSlug: string,
    task: string = '',
    version: number = 1,
    language: string = defaultLang.value,
  ): Promise<Exercise> => {
    const { selectGenre } = useGenre();
    const genre = selectGenre(genreSlug);
    const exercise = (await database
      .getData(generateId(genreSlug, task, version, language), { latest: true })
      .catch(() => createEmpty(genreSlug, task, version, language))) as Exercise;

    exercise.sections = genre.dispositions.reduce(
      (sections: Record<string, ExerciseSection>, disposition) => ({
        ...sections,
        [disposition.id]: exercise.sections[disposition.id] || createEmptySection(disposition),
      }),
      {},
    );

    state.exercise = exercise;

    return state.exercise;
  };

  const isEmpty = async (exercise) => {
    const dbEntry = await getBy(exercise.genre, exercise.task, exercise.version, exercise.language);
    const item = { ...exercise };

    delete dbEntry.createdAt;
    delete dbEntry.modifiedAt;
    delete item.createdAt;
    delete item.modifiedAt;

    return isEqual(dbEntry, item);
  };

  const save = async (exercise: Exercise, returnResult: boolean = true): Promise<Exercise> => {
    exercise.modifiedAt = Date.now();
    if (!exercise._id) {
      exercise._id = generateId(exercise.genre, exercise.task, exercise.version, exercise.language);
    }

    const result = await database.createOrUpdate({ ...exercise });
    await database.syncData(replicationOptions(), replicationToOptions());

    if (returnResult && result?.rev) {
      (exercise as Exercise & { _rev?: string })._rev = result.rev;
    }

    state.exercise = exercise;
    return exercise;
  };

  const saveAs = async (exercise: Exercise, slug: string, taskId?: string): Promise<Exercise> => {
    const newVersion = await generateVersion(slug, taskId);

    await database.createOrUpdate({
      ...exercise,
      _rev: null,
      _id: generateId(exercise.genre, exercise.task, newVersion, exercise.language),
      version: newVersion,
      modifiedAt: Date.now(),
    });
    await database.syncData(replicationOptions(), replicationToOptions());

    return await getBy(exercise.genre, exercise.task, newVersion, exercise.language);
  };

  const generateVersion = async (slug: string, taskId: string = '0'): Promise<number> => {
    const exercises: Exercise[] = await findBy(slug, taskId);

    return exercises.reduce((maxVersion, exercise) => Math.max(maxVersion, exercise.version), 0) + 1;
  };

  const generateExerciseLink = (slug: string, version?: number, taskId: string = '0'): string =>
    `/${slug}/write/${taskId}/${version}`;

  const remove = (
    genre: string,
    task: string = '',
    version: number = 1,
    language: string = defaultLang.value,
  ): Promise<Exercise> => database.removeData(generateId(genre, task, version, language));

  const removeById = (id: string): Promise<Core.Response> => database.removeData(id);

  const removeManyById = async (ids: string[]): Promise<Core.Response> => {
    try {
      const removed = await Promise.all(ids.map((id) => removeById(id)));
      await database.syncData(replicationOptions(), replicationToOptions());
      return removed;
    } catch (error) {
      console.error(error);
    }
  };

  const findBy = async (genre?: string, taskId?: string, language = defaultLang.value) => {
    const res = await database.getAllData({ include_docs: true, attachments: true, conflicts: true });

    const filtered = res.rows
      .map((row) => row.doc)
      .filter((doc) => {
        return (
          (!genre || doc.genre === genre) && (!taskId || doc.task === castTaskId(taskId)) && doc.language === language
        );
      });

    return filtered;
  };

  return {
    exists,
    findBy,
    generateExerciseLink,
    generateVersion,
    generateName,
    getBy,
    isEmpty,
    remove,
    removeById,
    removeManyById,
    save,
    saveAs,
    castTaskId,
    setLanguage,
    loadAllExercises,
    ...toRefs(state),
  };
};

let instance: ReturnType<typeof setup> = undefined;

export default () => {
  if (instance === undefined) {
    instance = setup();
  }

  return instance;
};
